import { Phone, MapPin, Sparkles, Layers, PaintBucket, Home } from 'lucide-react';
import Seo from '../components/Seo';
import Reveal from '../components/Reveal';
import ArrowLink from '../components/ArrowLink';
import PackageCard from '../components/PackageCard';
import FaqAccordion from '../components/FaqAccordion';
import { IMAGES, PACKAGES } from '../data/content';

const NEEDS = [
  {
    icon: Sparkles,
    title: 'A flawless finish deserves to be seen',
    text: 'Customers can’t judge your trowel work from a name on a van. Photos of glass-smooth walls and crisp corners on a proper website show the finish quality that wins the job — before you quote.',
  },
  {
    icon: MapPin,
    title: 'You show up for “plasterer near me”',
    text: 'A complete Google Business Profile with the right categories, service areas and photos is what puts a local plasterer on the map. We set it up properly and guide you through Google’s verification.',
  },
  {
    icon: Phone,
    title: 'Renovating customers can reach you instantly',
    text: 'Plastering enquiries often come mid-project, when a homeowner or builder needs someone reliable this month. Click-to-call and a simple enquiry form on every page mean they find you first.',
  },
];

const WORK_TYPES = [
  {
    icon: Layers,
    title: 'Skimming and re-skims',
    text: 'The bread-and-butter work that transforms tired rooms. Before-and-after photos of smooth-wall transformations are the single best advert a plasterer has.',
  },
  {
    icon: PaintBucket,
    title: 'Rendering and external work',
    text: 'K-rend, monocouche and traditional render — higher-value exterior jobs where a professional online presence reassures customers spending serious money.',
  },
  {
    icon: Home,
    title: 'Full renovations and new builds',
    text: 'Whole-house boarding and skimming for renovators and builders. A credible website gets you onto builders’ subcontractor lists, not just homeowner searches.',
  },
];

const PLASTERER_FAQS = [
  {
    q: 'Plastering is messy mid-job. What photos actually work?',
    a: 'Simple ones. A photo of the room before you start (artex, cracks, old wallpaper stripped back) and the same wall finished smooth — that pair of photos sells your skill better than anything else. Phone photos in daylight are genuinely all we need, and we’ll show you the angles that work.',
  },
  {
    q: 'Builders and homeowners both hire me. Can the site speak to both?',
    a: 'Yes. Homeowners want reassurance and photos of finished rooms; builders want to know you’re reliable, insured and available. Your services and enquiry setup cover both audiences without the site feeling split or cluttered.',
  },
  {
    q: 'I cover a whole county, not just one town. Can the site reflect that?',
    a: 'Yes. Your site lists the areas you cover in plain language, and your Google profile is set up as a service-area business, so you show up across your whole patch rather than being pinned to one address.',
  },
  {
    q: 'I’m on the hawk and trowel all day. How much of my time does this take?',
    a: 'About an hour in total, spread across the build: one plain-English questionnaire, your before-and-after photos from your phone, and a look at the finished site before launch. Everything else is handled for you.',
  },
];

export default function PlasterersPage() {
  return (
    <>
      <Seo
        title="Websites for Plasterers — Show Off a Flawless Finish | Build My Trade"
        description="Professional websites, Google Business Profile setup and social pages built specifically for UK plasterers. Fixed packages from £249, plain English, everything handled."
        path="/websites-for-plasterers"
        image={IMAGES.plastererHero.url}
      />

      {/* Hero */}
      <section className="bg-charcoal bg-blueprint" data-testid="plasterers-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-24 pt-32 sm:px-6 sm:pt-40 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">
              Websites for plasterers
            </p>
            <h1 className="mt-5 text-balance font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-offwhite sm:text-5xl lg:text-6xl">
              Glass-smooth walls. <span className="text-lime">An online presence to match.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-offwhite/70 sm:text-lg">
              Build My Trade sets up everything a plasterer needs to be found and booked locally: a professional
              website that shows off your smooth-wall transformations, a complete Google Business Profile and
              branded social pages — for one fixed, published price.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <ArrowLink to="/pricing" variant="lime" testid="plasterers-cta-packages" className="px-7 py-3.5">
                See Fixed-Price Packages
              </ArrowLink>
              <ArrowLink to="/contact" variant="outline-light" testid="plasterers-cta-quote" className="px-7 py-3.5">
                Get a Free Quote
              </ArrowLink>
            </div>
            <p className="mt-7 text-sm text-offwhite/50">
              Straightforward packages. Clear prices. No marketing jargon.
            </p>
          </div>
          <Reveal>
            <img
              src={IMAGES.plastererHero.url}
              alt={IMAGES.plastererHero.alt}
              loading="eager"
              className="aspect-[4/3] w-full rounded-2xl border border-charcoal-line object-cover"
            />
          </Reveal>
        </div>
      </section>

      {/* What a plasterer's online presence needs */}
      <section className="bg-offwhite" data-testid="plasterers-needs">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Built around how plastering work is won
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              What a plasterer’s website has to do
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {NEEDS.map((n, i) => (
              <Reveal key={i} delay={0.08 * i} className="h-full">
                <div className="h-full rounded-2xl border border-hairline bg-white p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-charcoal">
                    <n.icon className="h-5 w-5 text-lime" aria-hidden="true" />
                  </span>
                  <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-charcoal">{n.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stonebrand">{n.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Work types */}
      <section className="border-y border-hairline bg-white" data-testid="plasterers-work-types">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid items-start gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
                  From single walls to whole houses
                </p>
                <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
                  Set up to win every kind of plastering job
                </h2>
                <p className="mt-5 text-base leading-relaxed text-stonebrand">
                  One customer needs a ceiling re-skimmed; another is rendering a whole extension. Your website
                  speaks to both — and shows the finish quality that makes the choice easy.
                </p>
              </Reveal>
              <Reveal delay={0.15} className="mt-8">
                <img
                  src={IMAGES.plastererDetail.url}
                  alt={IMAGES.plastererDetail.alt}
                  loading="lazy"
                  className="aspect-[4/3] w-full rounded-2xl object-cover"
                />
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-5">
                {WORK_TYPES.map((w, i) => (
                  <Reveal key={i} delay={0.08 * i}>
                    <li className="flex gap-5 rounded-2xl border border-hairline bg-offwhite-light p-7">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-lime">
                        <w.icon className="h-5 w-5 text-charcoal" aria-hidden="true" />
                      </span>
                      <div>
                        <h3 className="font-heading text-lg font-bold tracking-tight text-charcoal">{w.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-stonebrand">{w.text}</p>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="bg-charcoal" data-testid="plasterers-packages">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">Fixed, published prices</p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
              Packages that fit a plastering business
            </h2>
            <p className="mt-5 text-base leading-relaxed text-offwhite/65">
              The same straightforward packages we offer every trade — one-off payments, everything listed, nothing
              hidden. Most plasterers start with Look Professional: a proper website with a before-and-after
              gallery, plus the full Google and social setup.
            </p>
          </Reveal>
          <div className="mt-16 grid items-stretch gap-6 lg:grid-cols-3">
            {PACKAGES.map((pkg, i) => (
              <PackageCard key={pkg.slug} pkg={pkg} delay={0.08 * i} />
            ))}
          </div>
        </div>
      </section>

      {/* Plasterer FAQs */}
      <section className="bg-offwhite" data-testid="plasterers-faqs">
        <div className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Questions plasterers ask us
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              Straight answers for busy plasterers
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <FaqAccordion items={PLASTERER_FAQS} testid="plasterers-faq" />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-charcoal-deep bg-blueprint" data-testid="plasterers-cta">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <h2 className="text-balance font-heading text-3xl font-extrabold tracking-tight text-offwhite sm:text-4xl">
                Ready for your finish to be the one <span className="text-lime">customers remember?</span>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-offwhite/65">
                Tell us about your plastering business and the areas you cover — we’ll recommend the right starting
                point, in plain English, with no obligation.
              </p>
              <div className="mt-9">
                <ArrowLink to="/contact" variant="lime" testid="plasterers-cta-final" className="px-8 py-4">
                  Get Your Free Quote
                </ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
